import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAxiosWithAuth } from '../../../common/api/hooks/useAxiosWithAuth';
import { URL } from '../../../common/api/urls';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { PREVIEW_IN_PROGRESS } from '../../../common/utility/constant';
import { useNavigate } from 'react-router-dom';
dayjs.extend(duration);

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '500px' },
  maxWidth: '500px',
  bgcolor: '#ffffff',
  borderRadius: '20px',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
  p: '30px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  animation: 'slideIn 0.5s ease',
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translate(-50%, -50%) translateY(-30px) scale(0.9)' },
    to: { opacity: 1, transform: 'translate(-50%, -50%) translateY(0) scale(1)' },
  },
};

// --- Preview Start Modal Component ---
function PreviewStartModal({ open, onClose, onConfirm, onOkay, instrument, isLoading, stats, timeLeft, isMarketLive, inProgress, inProgressInstrument, sameInstrumentInProgress }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-modal-title"
      aria-describedby="confirmation-modal-description"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.4s ease',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        },
      }}
    >
      {sameInstrumentInProgress ? (
        <Box sx={style}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: '16px' }}>
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
              }}
            >
              <OpenInNewIcon sx={{ fontSize: '36px', color: '#fff' }} />
            </Box>
          </Box>

          <Typography
            id="confirmation-modal-title"
            component="h2"
            sx={{
              fontSize: '26px',
              fontWeight: 700,
              color: '#2d3748',
              mb: '12px',
              textAlign: 'center',
              letterSpacing: '-0.5px',
            }}
          >
            Continue to Preview
          </Typography>

          <Typography
            sx={{
              color: '#4a5568',
              lineHeight: 1.7,
              mb: '24px',
              fontSize: '15px',
              textAlign: 'center',
              px: '16px',
            }}
          >
            A preview session for <b>{instrument}</b> is already active. The preview will open in a new Tab/Window.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: '12px',
              flexDirection: { xs: 'column', sm: 'row' },
              mt: '8px',
            }}
          >
            <Button
              variant="contained"
              onClick={() => onConfirm()}
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: '#fff',
                py: '13px',
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '15px',
                letterSpacing: '0.2px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
                  background: 'linear-gradient(135deg, #5a6fd8, #6a4190)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
              }}
            >
              Open Preview
            </Button>
          </Box>
        </Box>
      ) : !inProgress ? (
        <Box sx={style}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: '12px' }}>
            <Box
              sx={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
              }}
            >
              <PlayCircleOutlineIcon sx={{ fontSize: '32px', color: '#fff' }} />
            </Box>
          </Box>

        <Typography
          id="confirmation-modal-title"
          component="h2"
          sx={{
            fontSize: '26px',
            fontWeight: 700,
            color: '#2d3748',
            mb: '12px',
            textAlign: 'center',
            letterSpacing: '-0.5px',
          }}
        >
          Confirm Preview Start
        </Typography>

        <Typography
          id="confirmation-modal-description"
          sx={{
            color: '#4a5568',
            lineHeight: 1.7,
            mb: '24px',
            fontSize: '15px',
            textAlign: 'center',
            px: '8px',
          }}
        >
          {!isMarketLive 
            ? 'Market is not live. You can preview live Orderflow charts next time when the market is live. Preview is a feature that allows you to see a live Orderflwo Chart of the selected instrument for 10 minutes at a time.'
            : stats.number_of_previews_left === 0
            ? 'You have used all your preview chances for this week.'
            : (
              <>
                You have selected to start a quick <strong style={{ color: '#667eea' }}>10-minute Preview</strong> of Orderflow Charts of <strong>{instrument || 'NIFTY'}</strong>. Would you like to proceed?<br/><span style={{color: '#77621d'}}>Note: The preview will open in a new Tab/Window.</span>
              </>
            )
          }
        </Typography>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
            border: '1px solid #cbd5e0',
            borderRadius: '12px',
            p: '12px 14px',
            mb: '20px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: '18px', color: '#667eea', mt: '1px', flexShrink: 0 }} />
          <Typography
            sx={{
              color: '#4a5568',
              lineHeight: 1.6,
              fontSize: '13px',
              textAlign: 'left',
              fontWeight: 500,
            }}
          >
            You get only <strong>10 chances per week</strong> to preview any Instrument Orderflow charts for <strong>10 minutes</strong> at a time.
          </Typography>
        </Box>

        {/* --- Stats Section --- */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
            border: '1px solid #e9d5ff',
            borderRadius: '14px',
            p: '18px',
            mb: '24px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', mb: '14px' }}>
            <BarChartIcon sx={{ fontSize: '20px', color: '#667eea' }} />
            <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#2d3748', letterSpacing: '-0.3px' }}>
              Preview Summary
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '32px', mb: stats.number_of_previews_left === 0 ? '14px' : '0' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', mb: '4px' }}>
                <CheckCircleOutlineIcon sx={{ fontSize: '16px', color: '#48bb78' }} />
                <Typography sx={{ fontSize: '12px', color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Done
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#2d3748' }}>
                {stats.number_of_previews_done ?? '-'}
              </Typography>
            </Box>

            <Box sx={{ width: '1px', background: '#e2e8f0', my: '4px' }} />

            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', mb: '4px' }}>
                <HourglassEmptyIcon sx={{ fontSize: '16px', color: '#667eea' }} />
                <Typography sx={{ fontSize: '12px', color: '#718096', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Left
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: stats.number_of_previews_left === 0 ? '#e53e3e' : '#667eea' }}>
                {stats.number_of_previews_left ?? '-'}
              </Typography>
            </Box>
          </Box>

          {stats.number_of_previews_left === 0 && (
            <Box
              sx={{
                mt: '14px',
                pt: '14px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <AccessTimeIcon sx={{ fontSize: '18px', color: '#ed8936' }} />
              <Typography sx={{ fontSize: '13px', color: '#4a5568', fontWeight: 500 }}>
                New previews in:{' '}
                <strong style={{ color: '#ed8936', fontSize: '14px' }}>
                  {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m{' '}
                  {timeLeft.seconds !== undefined ? timeLeft.seconds : 0}s
                </strong>
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            flexDirection: { xs: 'column', sm: 'row' },
            mt: '8px',
          }}
        >
          {isMarketLive && stats.number_of_previews_left > 0 ? (
            <>
              <Button
                variant="contained"
                onClick={onConfirm}
                disabled={isLoading}
                sx={{
                  flex: 1,
                  background: isLoading ? '#9ca3af' : 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: '#fff',
                  py: '13px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '15px',
                  letterSpacing: '0.2px',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: isLoading ? 'none' : 'translateY(-2px)',
                    boxShadow: isLoading ? '0 4px 12px rgba(102, 126, 234, 0.25)' : '0 8px 20px rgba(102, 126, 234, 0.4)',
                    background: isLoading ? '#9ca3af' : 'linear-gradient(135deg, #5a6fd8, #6a4190)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  '&.Mui-disabled': {
                    background: '#9ca3af',
                    color: '#fff',
                  },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                    <span>Starting Preview...</span>
                  </Box>
                ) : (
                  'Yes, Start Preview'
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isLoading}
                sx={{
                  flex: 1,
                  color: isLoading ? '#9ca3af' : '#667eea',
                  border: isLoading ? '2px solid #9ca3af' : '2px solid #667eea',
                  py: '13px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '15px',
                  letterSpacing: '0.2px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: isLoading ? 'transparent' : '#667eea',
                    color: isLoading ? '#9ca3af' : '#fff',
                    transform: isLoading ? 'none' : 'translateY(-2px)',
                    boxShadow: isLoading ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.3)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  '&.Mui-disabled': {
                    border: '2px solid #9ca3af',
                    color: '#9ca3af',
                  },
                }}
              >
                Cancel
              </Button>
            </>
          ): (
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                flex: 1,
                color: '#667eea',
                border: '2px solid #667eea',
                py: '13px',
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '15px',
                letterSpacing: '0.2px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: '#667eea',
                  color: '#fff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
              }}
            >
              Okay
            </Button>
          )}
        </Box>
      </Box>) : (
        <Box sx={style}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: '16px' }}>
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ed8936, #f6ad55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 16px rgba(237, 137, 54, 0.3)',
            }}
          >
            <HourglassEmptyIcon sx={{ fontSize: '36px', color: '#fff' }} />
          </Box>
        </Box>

        <Typography
          id="confirmation-modal-title"
          component="h2"
          sx={{
            fontSize: '26px',
            fontWeight: 700,
            color: '#2d3748',
            mb: '12px',
            textAlign: 'center',
            letterSpacing: '-0.5px',
          }}
        >
          Preview Already Active
        </Typography>

        <Typography
          sx={{
            color: '#4a5568',
            lineHeight: 1.7,
            mb: '24px',
            fontSize: '15px',
            textAlign: 'center',
            px: '16px',
          }}
        >
          A preview session for <b>{inProgressInstrument}</b> is currently in progress. Please wait for it to complete before starting a new one.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            flexDirection: { xs: 'column', sm: 'row' },
            mt: '8px',
          }}
        >
            <Button
              variant="outlined"
              onClick={() => onOkay(isMarketLive)}
              sx={{
                flex: 1,
                color: '#667eea',
                border: '2px solid #667eea',
                py: '13px',
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '15px',
                letterSpacing: '0.2px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: '#667eea',
                  color: '#fff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
              }}
            >
              Okay
            </Button>
        </Box>
      </Box>
      )}
    </Modal>
  );
}

// --- Main Component ---
export default function PreviewStartPopup(props) {
  const [
    cancelPreview,
    responsePreview,
    errorPreview,
    loadedPreview,
    resetPreview,
    getPreviewStatus,
  ] = useAxiosWithAuth();

  const [
    cancelPreviewStats,
    responsePreviewStats,
    errorPreviewStats,
    loadedPreviewStats,
    resetPreviewStats,
    getPreviewStatusStats,
  ] = useAxiosWithAuth();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [stats, setStats] = React.useState({});
  const [timeLeft, setTimeLeft] = React.useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isMarketLive, setIsMarketLive] = React.useState(false);
  const [inProgress, setInProgress] = React.useState(false);
  const [inProgressInstrument, setInProgressInstrument] = React.useState(null);
  const [sameInstrumentInProgress, setSameInstrumentInProgress] = React.useState(false);
  const [dataLoaded, setDataLoaded] = React.useState(false);
  const navigate = useNavigate();
  
  const instrument = props.instrument;

  React.useEffect(() => {
    if (props.open && !dataLoaded) {
      console.log('Fetching preview stats...');
      getPreviewStatusStats(`${URL.OF_PREVIEW_STATS}`, 'POST', {});
    }
  }, [props.open]);

  React.useEffect(() => {
    if (loadedPreviewStats && responsePreviewStats) {
      console.log('Preview stats response =', responsePreviewStats);
      setStats(responsePreviewStats);
      setIsMarketLive(responsePreviewStats.is_market_live || false);
      
      if (responsePreviewStats.status === PREVIEW_IN_PROGRESS) {
        if (responsePreviewStats.instrument === instrument) {
          setSameInstrumentInProgress(true);
        } else {
          setInProgress(true);
          setInProgressInstrument(responsePreviewStats.instrument);
        }
      }
      
      if (responsePreviewStats.days_for_new_previews) {
        setTimeLeft(responsePreviewStats.days_for_new_previews);
      }
      
      setDataLoaded(true);
      setModalOpen(true); 
      resetPreviewStats();
    } else if (errorPreviewStats !== null) {
      console.error('Error fetching preview stats:', errorPreviewStats);
      resetPreviewStats();
      props.onClose();
    }
  }, [loadedPreviewStats, responsePreviewStats, errorPreviewStats]);

  React.useEffect(() => {
    if (!stats.days_for_new_previews) return;
    
    const targetTime = dayjs()
      .add(stats.days_for_new_previews.days, 'days')
      .add(stats.days_for_new_previews.hours, 'hours')
      .add(stats.days_for_new_previews.minutes, 'minutes')
      .add(stats.days_for_new_previews.seconds || 0, 'seconds');
    
    const interval = setInterval(() => {
      const now = dayjs();
      const diff = targetTime.diff(now);
      
      if (diff <= 0) {
        clearInterval(interval);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const dur = dayjs.duration(diff);
      setTimeLeft({
        days: Math.floor(dur.asDays()),
        hours: dur.hours(),
        minutes: dur.minutes(),
        seconds: dur.seconds(),
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [stats.days_for_new_previews]);

  const startPreview = () => {
    getPreviewStatus(`${URL.OF_PREVIEW}`, 'POST', { instrument });
  };

  const openOFChart = () => {
    const url = `/orderflow-preview?symbol=${encodeURIComponent(instrument)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  React.useEffect(() => {
    if (loadedPreview) {
      openOFChart();
      resetPreview();
      setIsLoading(false);
      setModalOpen(false);
      setDataLoaded(false);
      setSameInstrumentInProgress(false);
      props.onClose();
    } else if (errorPreview !== null) {
      resetPreview();
      setIsLoading(false);
    }
  }, [loadedPreview, responsePreview, errorPreview]);

  const handleClose = () => {
    setModalOpen(false);
    setDataLoaded(false);
    setSameInstrumentInProgress(false);
    props.onClose();
  };

  const handleOkay = (isMarketLive) => {
    if (isMarketLive) {
      openOFChart();
      resetPreview();
    }
    setModalOpen(false);
    setDataLoaded(false);
    setSameInstrumentInProgress(false);
    props.onClose();
  };

  const handleConfirm = () => {
    setIsLoading(true);
    startPreview();
  };

  return (
    <PreviewStartModal
      open={modalOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      onOkay={handleOkay}
      instrument={instrument}
      isLoading={isLoading}
      stats={stats}
      timeLeft={timeLeft}
      isMarketLive={isMarketLive}
      inProgress={inProgress}
      inProgressInstrument={inProgressInstrument}
      sameInstrumentInProgress={sameInstrumentInProgress}
    />
  );
}